/**
 * Coordinates the local UI side of an Agent message submission. Transport,
 * streaming and attachment upload remain owned by their existing facades.
 */
export function createMessageSubmissionController({
  elements,
  attachmentController,
  messageStore,
  sessionStore,
  messageFacade,
  canvasStore,
  memoryFacade,
  artifactFacade,
  renderMessages,
  updateCounter,
  showToast,
  friendlyAgentError,
  publishAgentEvent,
  syncRemoteSession,
  getLocalStreamingSessionId,
  setLocalStreamingSessionId,
  setTimeoutFn = setTimeout,
}) {
  const { textarea, sendButton } = elements;
  let bound = false;

  function pendingAttachments() {
    return attachmentController.getPendingAttachments().map((attachment) => ({
      id: attachment.id,
      filePath: attachment.filePath,
      originalName: attachment.originalName,
      mimeType: attachment.mimeType,
      size: attachment.size,
    }));
  }

  async function send(text, retryAttachments = null) {
    const currentSession = sessionStore.getSessionState().currentSession;
    if (!currentSession) {
      showToast('请先选择或新建会话', 'error');
      return;
    }
    const attachments = Array.isArray(retryAttachments) ? retryAttachments : pendingAttachments();
    // AFV-002：服务端接受请求（session_ready）前失败时，恢复输入与附件，避免用户内容丢失。
    const draftAttachments = Array.isArray(retryAttachments) ? null : attachmentController.getPendingAttachments();
    let accepted = false;
    const clientUserMessageId = messageStore.appendUserMessage(text, attachments.length > 0 ? attachments : null);
    const restoreDraft = () => {
      messageStore.removeMessage(clientUserMessageId);
      renderMessages();
      if (textarea && !textarea.value.trim() && text) {
        textarea.value = text;
        updateCounter();
      }
      if (draftAttachments?.length) attachmentController.restorePendingAttachments(draftAttachments);
    };
    renderMessages();
    if (sendButton) sendButton.disabled = true;
    if (!Array.isArray(retryAttachments)) attachmentController.clearPendingAttachments();
    try {
      setLocalStreamingSessionId(currentSession.id);
      await messageFacade.sendAgentMessage({
        sessionId: currentSession.id,
        content: text,
        attachments: attachments.length > 0 ? attachments : undefined,
        artifactIds: canvasStore.getCanvasState().artifacts.flatMap((artifact) => artifact.memberIds || [artifact.id]),
        clientUserMessageId,
        onError: (error) => {
          if (error?.name === 'AbortError') return;
          showToast(friendlyAgentError(error), 'error');
          if (!accepted) restoreDraft();
        },
        onStreamEvent: (event) => {
          if (event.event === 'session_ready') accepted = true;
          publishAgentEvent(currentSession.id, event);
          if (event.event === 'tool_result' && event.name === 'update_working_memory' && !event.error_message) {
            memoryFacade.loadMemoryItems();
            artifactFacade.loadArtifacts(currentSession.id);
          }
        },
      });
    } finally {
      if (String(getLocalStreamingSessionId()) === String(currentSession.id)) {
        setLocalStreamingSessionId(null);
      }
      setTimeoutFn(() => syncRemoteSession(currentSession.id), 250);
      if (sendButton) sendButton.disabled = false;
      updateCounter();
    }
  }

  function submit() {
    const text = textarea?.value.trim() || '';
    if (!text && attachmentController.getPendingAttachments().length === 0) return;
    if (text.length > 8000) {
      // AFV-005：超长输入不再静默忽略，明确告知上限与当前字数。
      showToast(`输入超过 8000 字上限（当前 ${text.length} 字），请精简后发送`, 'error');
      return;
    }
    if (textarea) textarea.value = '';
    updateCounter();
    send(text).catch(() => {});
  }

  function handleKeydown(event) {
    if (event.isComposing || event.keyCode === 229 || event.key !== 'Enter') return;
    if (event.metaKey || event.ctrlKey) {
      event.preventDefault();
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      textarea.value = textarea.value.substring(0, start) + '\n' + textarea.value.substring(end);
      textarea.selectionStart = textarea.selectionEnd = start + 1;
      updateCounter();
      return;
    }
    event.preventDefault();
    submit();
  }

  function bind() {
    if (bound) return;
    textarea?.addEventListener?.('input', updateCounter);
    textarea?.addEventListener?.('keydown', handleKeydown);
    sendButton?.addEventListener?.('click', submit);
    bound = true;
  }

  function destroy() {
    if (!bound) return;
    textarea?.removeEventListener?.('input', updateCounter);
    textarea?.removeEventListener?.('keydown', handleKeydown);
    sendButton?.removeEventListener?.('click', submit);
    bound = false;
  }

  return { bind, destroy, submit, send };
}
